import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useAuth } from '../context/AuthContext';
import {
  getCompany,
  updateCompany,
  testNrfCloudConnection,
  syncCompanyDevices,
  getCompanyUsers,
  getCompanyDevices,
  getBillingSyncLogs,
  getCompanyAuditLogs,
  syncCompanyBilling,
} from '../services/companies';
import { isSuperAdmin, isCompanyAdmin, ROLE_LABELS, getEffectiveRole } from '../services/users';
import {
  getGatewaysByCompany,
  getUnclaimedGateways,
  checkGatewayHealth,
  updateGateway,
  createGateway,
  deleteGateway,
} from '../services/gateways';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StatsGrid,
  StatCard,
  DetailCard,
  DetailGrid,
  StatusChip,
  TableContainer,
  StyledTable,
  Th,
  Td,
  Button,
  LinkButton,
  FormGroup,
  Label,
  Input,
  Select,
  HelperText,
  EmptyState,
  LoadingText,
  MutedText,
  InlineCode,
  SectionTitle,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertBanner,
  Stack,
  SpaceBetween,
  Row,
  H2Title,
} from '../components/styled';
import { IconX } from '@tabler/icons-react';
import { SegmentedTabs } from '../components/SegmentedTabs';

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  &:hover {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const DeviceLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const DescriptionText = styled.p`
  color: ${({ theme }) => theme.font.color.tertiary};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const InactiveRow = styled.tr`
  opacity: 0.5;
`;

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const HealthComponentCard = styled.div`
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const HealthComponentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

const HealthDot = styled('span', {
  shouldForwardProp: prop => prop !== 'connected'
})<{ connected?: boolean }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ connected, theme }) =>
    connected ? theme.color.green : connected === false ? theme.color.red : theme.font.color.tertiary};
`;

const HealthDetail = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

// Truncated table cell with ellipsis. Width-bounded variants for the
// gateway list (name / hostname / endpoint columns).
const TruncatedTd = styled(Td)<{ $maxWidth: string }>`
  max-width: ${({ $maxWidth }) => $maxWidth};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoWrapTd = styled(Td)`
  white-space: nowrap;
`;

// Padding tweak for the expanded row showing gateway diagnostics inline.
const ExpandedDetailTd = styled.td`
  padding: ${({ theme }) => theme.spacing(2, 4)};
`;

// A spacing-utility wrapper that adds bottom margin to a header section.
// Used in places that previously had style={{ marginBottom: '1.5rem' }}.
const SpacedSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

// Pushes a child to the far right of a flex container (replaces inline
// style={{ marginLeft: 'auto' }} on the Dismiss button).
const PushRight = styled.span`
  margin-left: auto;
`;

// Spacing utility for MarginedTitle — replaces inline marginTop on
// <SectionTitle>.
const MarginedSectionTitle = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

// Wraps the audit log block; replaces an inline marginTop.
const AuditLogWrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

// AlertBanner gets its own bottom margin in some places — replaces
// scattered inline marginBottom: 16px / 1rem.
const SpacedAlert = styled(AlertBanner)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

// TableContainer with bottom margin baked in — replaces inline
// style={{ marginBottom: '1.5rem' }} in the Claim Gateway modal.
const SpacedTableContainer = styled(TableContainer)`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

// Sync Billing Now CTA gets a top margin to clear the description above it.
const TopSpacedButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

// Pointer cursor for clickable table rows in the unclaimed-gateways list.
const ClickableRow = styled.tr`
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.background.tertiary};
  }
`;

function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  const userIsSuperAdmin = isSuperAdmin(user);
  const userIsCompanyAdmin = isCompanyAdmin(user);

  const [settingsForm, setSettingsForm] = useState({
    name: '',
    billing_method: 'none',
    price_per_device: '',
  });
  const [settingsInitialized, setSettingsInitialized] = useState(false);

  // Gateways tab state (super-admin lifecycle controls)
  const [healthResults, setHealthResults] = useState<Record<number, { type: string; data?: any; message?: string }>>({});
  const [showCreateGatewayModal, setShowCreateGatewayModal] = useState(false);
  const [createGatewayForm, setCreateGatewayForm] = useState({ name: '', provider: 'nrf_cloud', endpointUrl: '' });
  const [editGatewayTarget, setEditGatewayTarget] = useState<any | null>(null);
  const [editGatewayForm, setEditGatewayForm] = useState({ name: '', endpointUrl: '' });
  const [deleteGatewayTarget, setDeleteGatewayTarget] = useState<any | null>(null);
  const [claimingOpen, setClaimingOpen] = useState(false);
  const [selectedUnclaimed, setSelectedUnclaimed] = useState<any>(null);
  const [claimForm, setClaimForm] = useState({ apiKey: '', teamId: '' });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: company, isLoading, error: loadError } = useQuery({
    queryKey: ['company', id],
    queryFn: () => getCompany(id),
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['company-users', id],
    queryFn: () => getCompanyUsers(id, { limit: 100 }),
    enabled: activeTab === 'users',
  });

  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['company-devices', id],
    queryFn: () => getCompanyDevices(id, { limit: 100 }),
    enabled: activeTab === 'devices',
  });

  const { data: billingLogs, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-logs', id],
    queryFn: () => getBillingSyncLogs(id, { limit: 20 }),
    enabled: activeTab === 'billing',
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['company-audit', id],
    queryFn: () => getCompanyAuditLogs(id, { limit: 50 }),
    enabled: activeTab === 'activity' && userIsCompanyAdmin,
  });

  // Gateways tab queries
  const { data: gateways = [], isLoading: gatewayLoading } = useQuery({
    queryKey: ['company-gateways', id],
    queryFn: () => getGatewaysByCompany(id),
    enabled: activeTab === 'gateways',
    refetchInterval: 30000,
  });

  const { data: unclaimedGateways = [], isLoading: unclaimedLoading } = useQuery({
    queryKey: ['gateways-unclaimed'],
    queryFn: () => getUnclaimedGateways(),
    enabled: claimingOpen,
  });

  // Company mutations
  const testConnectionMutation = useMutation({
    mutationFn: () => testNrfCloudConnection(id),
    onSuccess: (data) => {
      if (data.success) {
        setNotification({ type: 'success', text: `Connection successful — Team: ${data.teamName} (${data.teamId})` });
      } else {
        setNotification({ type: 'error', text: `Connection failed: ${data.error}` });
      }
    },
    onError: (err) => setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to test connection' }),
  });

  const syncDevicesMutation = useMutation({
    mutationFn: () => syncCompanyDevices(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['company-devices', id]);
      queryClient.invalidateQueries(['company', id]);
      const parts = [`${data.created} created`, `${data.updated} updated`];
      if (data.fetched !== undefined) parts.unshift(`${data.fetched} fetched`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      setNotification({ type: data.skipped ? 'error' : 'success', text: `Sync complete: ${parts.join(', ')}` });
    },
    onError: (err) => setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to sync devices' }),
  });

  const syncBillingMutation = useMutation({
    mutationFn: () => syncCompanyBilling(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['billing-logs', id]);
      setNotification({ type: 'success', text: 'Billing sync complete' });
    },
    onError: (err) => setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to sync billing' }),
  });

  const updateCompanyMutation = useMutation({
    mutationFn: (data) => updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['company', id]);
      setNotification({ type: 'success', text: 'Company settings updated' });
    },
    onError: (err) => setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to update company' }),
  });

  // Integration tab mutations
  const healthMutation = useMutation({
    mutationFn: (gatewayId: number) => checkGatewayHealth(gatewayId),
    onSuccess: (data, gatewayId) => {
      setHealthResults((prev) => ({ ...prev, [gatewayId]: { type: 'success', data } }));
      queryClient.invalidateQueries({ queryKey: ['company-gateways', id] });
    },
    onError: (err: any, gatewayId) => {
      setHealthResults((prev) => ({ ...prev, [gatewayId]: { type: 'error', message: err.response?.data?.error || err.message } }));
    },
  });

  const updateGatewayMutation = useMutation({
    mutationFn: ({ gatewayId, ...data }: any) => updateGateway(gatewayId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-gateways', id] });
      setNotification({ type: 'success', text: 'Gateway updated successfully' });
    },
    onError: (err: any) => {
      setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to update gateway' });
    },
  });

  const deleteGatewayMutation = useMutation({
    mutationFn: (gatewayId: number) => deleteGateway(gatewayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-gateways', id] });
      setDeleteGatewayTarget(null);
      setNotification({ type: 'success', text: 'Gateway deleted' });
    },
    onError: (err: any) => {
      setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to delete gateway' });
    },
  });

  const createGatewayMutation = useMutation({
    mutationFn: (data: { companyId: string; name: string; provider: string; endpointUrl?: string }) => createGateway(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-gateways', id] });
      setShowCreateGatewayModal(false);
      setCreateGatewayForm({ name: '', provider: 'nrf_cloud', endpointUrl: '' });
      setNotification({ type: 'success', text: 'Gateway created successfully' });
    },
    onError: (err: any) => {
      setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to create gateway' });
    },
  });

  const claimGatewayMutation = useMutation({
    mutationFn: ({ gatewayId, companyId, credentials }: { gatewayId: number; companyId: string; credentials?: { apiKey?: string; teamId?: string } }) =>
      updateGateway(gatewayId, { companyId, ...(credentials?.apiKey ? { credentials } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-gateways', id] });
      queryClient.invalidateQueries({ queryKey: ['gateways-unclaimed'] });
      setClaimingOpen(false);
      setSelectedUnclaimed(null);
      setClaimForm({ apiKey: '', teamId: '' });
      setNotification({ type: 'success', text: 'Gateway claimed successfully' });
    },
    onError: (err: any) => {
      setNotification({ type: 'error', text: err.response?.data?.error || 'Failed to claim gateway' });
    },
  });

  if (company && !settingsInitialized) {
    setSettingsForm({
      name: company.name || '',
      billing_method: company.billing_method || 'none',
      price_per_device: company.price_per_device || '',
    });
    setSettingsInitialized(true);
  }

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    const data = { ...settingsForm };
    if (data.price_per_device === '' || data.price_per_device === null) {
      data.price_per_device = 0;
    }
    updateCompanyMutation.mutate(data);
  };

  const users = usersData?.users || [];
  const devices = devicesData?.devices || [];
  const auditLogs = auditData?.logs || [];
  const syncLogs = billingLogs?.logs || [];

  if (company && user) {
    const canAccess = userIsSuperAdmin || user.company_id === company.id;
    if (!canAccess) {
      navigate('/dashboard');
      return null;
    }
  }

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingText>Loading company...</LoadingText>
      </PageContainer>
    );
  }

  if (loadError || !company) {
    return (
      <PageContainer>
        <EmptyState>
          <p>Company not found</p>
          <p><Link to="/software-providers">Back to Software Providers</Link></p>
        </EmptyState>
      </PageContainer>
    );
  }

  const deployedCount = company.deployed_device_count || devices.filter(d => d.deployed).length;

  const roleBadgeVariant = (role) => {
    switch (role) {
      case 'super_admin':  return 'purple';
      case 'company_admin': return 'blue';
      default:              return 'gray';
    }
  };

  const statusVariant = (status) => {
    switch (status) {
      case 'active':  return 'green';
      case 'error':   return 'red';
      case 'pending': return 'yellow';
      default:        return 'gray';
    }
  };

  return (
    <PageContainer>
      {userIsSuperAdmin && (
        <BackLink to="/software-providers">&larr; Back to Software Providers</BackLink>
      )}

      <PageHeader>
        <PageTitle>{company.name}</PageTitle>
        <HeaderActions>
          {company.nrf_cloud_team_id && (
            <>
              <Button
                variant="secondary"
                onClick={() => testConnectionMutation.mutate()}
                disabled={testConnectionMutation.isPending}
              >
                {testConnectionMutation.isPending ? 'Testing...' : 'Test nRF Cloud'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => syncDevicesMutation.mutate()}
                disabled={syncDevicesMutation.isPending}
              >
                {syncDevicesMutation.isPending ? 'Syncing...' : 'Sync Devices'}
              </Button>
            </>
          )}
        </HeaderActions>
      </PageHeader>

      {notification && (
        <AlertBanner variant={notification.type === 'success' ? 'success' : 'error'}>
          {notification.text}
          <PushRight><LinkButton onClick={() => setNotification(null)}>Dismiss</LinkButton></PushRight>
        </AlertBanner>
      )}

      <SegmentedTabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'users', label: `Users (${company.user_count || 0})` },
          { id: 'devices', label: `Devices (${company.device_count || 0})` },
          ...(userIsSuperAdmin ? [{ id: 'gateways', label: 'Gateways' }] : []),
          ...(userIsCompanyAdmin
            ? [
                { id: 'billing', label: 'Billing' },
                { id: 'activity', label: 'Activity' },
                { id: 'settings', label: 'Settings' },
              ]
            : []),
        ]}
        activeId={activeTab}
        onChange={setActiveTab}
      />

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <StatsGrid>
            <StatCard>
              <h3>Users</h3>
              <p className="stat-value">{company.user_count || 0}</p>
            </StatCard>
            <StatCard>
              <h3>Total Devices</h3>
              <p className="stat-value">{company.device_count || 0}</p>
            </StatCard>
            <StatCard>
              <h3>Deployed</h3>
              <p className="stat-value">{deployedCount}</p>
            </StatCard>
            <StatCard>
              <h3>Price/Device</h3>
              <p className="stat-value">${parseFloat(company.price_per_device || 0).toFixed(2)}</p>
            </StatCard>
          </StatsGrid>

          <DetailGrid>
            <DetailCard>
              <h2>Company Information</h2>
              <dl>
                <dt>Company ID</dt>
                <dd><InlineCode>{company.id}</InlineCode></dd>
                <dt>Name</dt>
                <dd>{company.name}</dd>
                <dt>Created</dt>
                <dd>{new Date(company.created_at).toLocaleDateString()}</dd>
                <dt>Billing Method</dt>
                <dd>
                  <StatusChip variant={company.billing_method === 'stripe' ? 'green' : company.billing_method === 'odoo' ? 'blue' : 'gray'}>
                    {company.billing_method || 'None'}
                  </StatusChip>
                </dd>
              </dl>
            </DetailCard>

            <DetailCard>
              <h2>nRF Cloud Integration</h2>
              <dl>
                <dt>Status</dt>
                <dd>
                  {company.nrf_cloud_team_id ? (
                    <StatusChip variant="positive">Connected</StatusChip>
                  ) : (
                    <StatusChip variant="neutral">Not configured</StatusChip>
                  )}
                </dd>
                {company.nrf_cloud_team_id && (
                  <>
                    <dt>Team ID</dt>
                    <dd><InlineCode>{company.nrf_cloud_team_id}</InlineCode></dd>
                  </>
                )}
              </dl>
            </DetailCard>
          </DetailGrid>
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {usersLoading ? (
            <LoadingText>Loading users...</LoadingText>
          ) : users.length === 0 ? (
            <EmptyState>
              <p>No users in this company</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th>Created</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const Row = u.is_active ? 'tr' : InactiveRow;
                    return (
                      <Row key={u.id}>
                        <Td>{u.name}</Td>
                        <Td>{u.email}</Td>
                        <Td>
                          <StatusChip variant={roleBadgeVariant(getEffectiveRole(u))}>
                            {ROLE_LABELS[getEffectiveRole(u)] || getEffectiveRole(u)}
                          </StatusChip>
                        </Td>
                        <Td>
                          <StatusChip variant={u.is_active ? 'green' : 'red'}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </StatusChip>
                        </Td>
                        <Td>{new Date(u.created_at).toLocaleDateString()}</Td>
                      </Row>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <>
          {devicesLoading ? (
            <LoadingText>Loading devices...</LoadingText>
          ) : devices.length === 0 ? (
            <EmptyState>
              <p>No devices in this company</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Device ID</Th>
                    <Th>Status</Th>
                    <Th>Deployed</Th>
                    <Th>Last Seen</Th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id}>
                      <Td>
                        <DeviceLink to={`/devices/${device.id}`}>
                          {device.name || device.device_id}
                        </DeviceLink>
                      </Td>
                      <Td><InlineCode>{device.device_id}</InlineCode></Td>
                      <Td>
                        <StatusChip variant={device.status === 'online' ? 'green' : 'red'}>
                          {device.status}
                        </StatusChip>
                      </Td>
                      <Td>
                        <StatusChip variant={device.deployed ? 'green' : 'gray'}>
                          {device.deployed ? 'Yes' : 'No'}
                        </StatusChip>
                      </Td>
                      <Td>
                        {device.last_seen
                          ? new Date(device.last_seen).toLocaleString()
                          : 'Never'}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Gateways Tab */}
      {activeTab === 'gateways' && userIsSuperAdmin && (
        <>
          <SpacedSection>
            <SpaceBetween>
              <Row gap={3}>
                <Button onClick={() => setShowCreateGatewayModal(true)}>
                  Create Gateway
                </Button>
                <Button variant="secondary" onClick={() => setClaimingOpen(true)}>
                  Claim Gateway
                </Button>
              </Row>
            </SpaceBetween>
          </SpacedSection>

          {gatewayLoading ? (
            <LoadingText>Loading gateways...</LoadingText>
          ) : gateways.length === 0 ? (
            <EmptyState>
              <p>No gateways configured for this company</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Hostname</Th>
                    <Th>IP Address</Th>
                    <Th>Endpoint</Th>
                    <Th>Status</Th>
                    <Th>Cloud</Th>
                    <Th>DB</Th>
                    <Th>Last Check</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {gateways.map((gw) => {
                    const gwHealth = gw.health_status;
                    const gwDbComponent = gwHealth?.components?.database;
                    const healthResult = healthResults[gw.id];
                    return (
                      <React.Fragment key={gw.id}>
                        <tr>
                          <TruncatedTd $maxWidth="160px">{gw.name}</TruncatedTd>
                          <TruncatedTd $maxWidth="140px">{gw.hostname || <MutedText>—</MutedText>}</TruncatedTd>
                          <Td>{gw.ip_address || <MutedText>—</MutedText>}</Td>
                          <TruncatedTd $maxWidth="200px">
                            {gw.endpoint_url
                              ? <InlineCode title={gw.endpoint_url}>{gw.endpoint_url}</InlineCode>
                              : <MutedText>Not set</MutedText>
                            }
                          </TruncatedTd>
                          <Td>
                            <StatusChip variant={statusVariant(gw.status)}>
                              {gw.status}
                            </StatusChip>
                          </Td>
                          <Td>
                            <StatusChip variant={gw.hasCredentials ? 'positive' : 'neutral'}>
                              {gw.hasCredentials ? 'Set' : 'None'}
                            </StatusChip>
                          </Td>
                          <Td>
                            {gwDbComponent ? (
                              <StatusChip variant={gwDbComponent.connected ? 'positive' : 'critical'}>
                                {gwDbComponent.connected ? 'OK' : 'Error'}
                              </StatusChip>
                            ) : <MutedText>—</MutedText>}
                          </Td>
                          <Td>
                            {gw.last_health_check
                              ? new Date(gw.last_health_check).toLocaleDateString()
                              : <MutedText>Never</MutedText>
                            }
                          </Td>
                          <NoWrapTd>
                            <Row gap={2}>
                              <LinkButton
                                onClick={() => healthMutation.mutate(gw.id)}
                                disabled={healthMutation.isPending && healthMutation.variables === gw.id}
                              >
                                {healthMutation.isPending && healthMutation.variables === gw.id ? 'Checking…' : 'Health'}
                              </LinkButton>
                              <LinkButton
                                onClick={() => {
                                  setEditGatewayTarget(gw);
                                  setEditGatewayForm({ name: gw.name, endpointUrl: gw.endpoint_url || '' });
                                }}
                              >
                                Edit
                              </LinkButton>
                              <LinkButton
                                variant="danger"
                                onClick={() => setDeleteGatewayTarget(gw)}
                              >
                                Delete
                              </LinkButton>
                            </Row>
                          </NoWrapTd>
                        </tr>
                        {healthResult && (
                          <tr>
                            <ExpandedDetailTd colSpan={9}>
                              <AlertBanner
                                variant={healthResult.type === 'success' ? (healthResult.data?.healthy ? 'success' : 'error') : 'error'}
                              >
                                {healthResult.type === 'success'
                                  ? `Health: ${healthResult.data.healthy ? 'All systems healthy' : 'Issues detected'}`
                                  : `Health check failed: ${healthResult.message}`
                                }
                              </AlertBanner>
                            </ExpandedDetailTd>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}

          {/* Claim Gateway Modal */}
          {claimingOpen && (
            <ModalOverlay onClick={() => { setClaimingOpen(false); setSelectedUnclaimed(null); setClaimForm({ apiKey: '', teamId: '' }); }}>
              <ModalCard size="lg" onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <H2Title>Claim Gateway</H2Title>
                  <ModalCloseButton
                    onClick={() => { setClaimingOpen(false); setSelectedUnclaimed(null); setClaimForm({ apiKey: '', teamId: '' }); }}
                    aria-label="Close"
                  >
                    <IconX size={18} stroke={2} />
                  </ModalCloseButton>
                </ModalHeader>
                <ModalBody>
                  {unclaimedLoading ? (
                    <LoadingText>Loading unclaimed gateways...</LoadingText>
                  ) : unclaimedGateways.length === 0 ? (
                    <EmptyState>
                      <p>No unclaimed gateways available</p>
                      <MutedText>Boot a gateway VM without a company assignment to see it here.</MutedText>
                    </EmptyState>
                  ) : (
                    <SpacedTableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <Th></Th>
                            <Th>Name</Th>
                            <Th>Hostname</Th>
                            <Th>IP Address</Th>
                            <Th>First Seen</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {unclaimedGateways.map((gw) => (
                            <ClickableRow
                              key={gw.id}
                              onClick={() => setSelectedUnclaimed(gw)}
                            >
                              <Td>
                                <input
                                  type="radio"
                                  checked={selectedUnclaimed?.id === gw.id}
                                  onChange={() => setSelectedUnclaimed(gw)}
                                />
                              </Td>
                              <Td>{gw.name}</Td>
                              <Td>{gw.hostname || <MutedText>—</MutedText>}</Td>
                              <Td>{gw.ip_address || <MutedText>—</MutedText>}</Td>
                              <Td>{new Date(gw.created_at).toLocaleString()}</Td>
                            </ClickableRow>
                          ))}
                        </tbody>
                      </StyledTable>
                    </SpacedTableContainer>
                  )}

                  {selectedUnclaimed && (
                    <Stack gap={3}>
                      <FormGroup>
                        <Label>nRF Cloud API Key (optional)</Label>
                        <Input
                          type="password"
                          value={claimForm.apiKey}
                          onChange={(e) => setClaimForm((prev) => ({ ...prev, apiKey: e.target.value }))}
                          placeholder="Leave blank to configure later"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>nRF Cloud Team ID (optional)</Label>
                        <Input
                          value={claimForm.teamId}
                          onChange={(e) => setClaimForm((prev) => ({ ...prev, teamId: e.target.value }))}
                          placeholder="Leave blank to configure later"
                        />
                      </FormGroup>
                    </Stack>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondary" onClick={() => { setClaimingOpen(false); setSelectedUnclaimed(null); setClaimForm({ apiKey: '', teamId: '' }); }}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!selectedUnclaimed || claimGatewayMutation.isPending}
                    onClick={() => {
                      if (!selectedUnclaimed) return;
                      const credentials = claimForm.apiKey
                        ? { apiKey: claimForm.apiKey, ...(claimForm.teamId ? { teamId: claimForm.teamId } : {}) }
                        : undefined;
                      claimGatewayMutation.mutate({ gatewayId: selectedUnclaimed.id, companyId: id!, credentials });
                    }}
                  >
                    {claimGatewayMutation.isPending ? 'Claiming...' : 'Claim Gateway'}
                  </Button>
                </ModalFooter>
              </ModalCard>
            </ModalOverlay>
          )}
        </>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && userIsCompanyAdmin && (
        <>
          <DetailGrid>
            <DetailCard>
              <h2>Billing Configuration</h2>
              <dl>
                <dt>Method</dt>
                <dd>
                  <StatusChip variant={company.billing_method === 'stripe' ? 'green' : company.billing_method === 'odoo' ? 'blue' : 'gray'}>
                    {company.billing_method || 'None'}
                  </StatusChip>
                </dd>
                <dt>Price per Device</dt>
                <dd>${parseFloat(company.price_per_device || 0).toFixed(2)}/month</dd>
                <dt>Deployed Devices</dt>
                <dd>{deployedCount}</dd>
                <dt>Monthly Total</dt>
                <dd>${(deployedCount * parseFloat(company.price_per_device || 0)).toFixed(2)}</dd>
              </dl>
              {company.billing_method && company.billing_method !== 'none' && (
                <TopSpacedButton
                  variant="secondary"
                  onClick={() => syncBillingMutation.mutate()}
                  disabled={syncBillingMutation.isPending}
                >
                  {syncBillingMutation.isPending ? 'Syncing...' : 'Sync Billing Now'}
                </TopSpacedButton>
              )}
            </DetailCard>
          </DetailGrid>

          <MarginedSectionTitle>Sync History</MarginedSectionTitle>
          {billingLoading ? (
            <LoadingText>Loading sync history...</LoadingText>
          ) : syncLogs.length === 0 ? (
            <EmptyState>
              <p>No billing sync history</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>Devices</Th>
                    <Th>Status</Th>
                    <Th>Error</Th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs.map((log) => (
                    <tr key={log.id}>
                      <Td>{new Date(log.created_at).toLocaleString()}</Td>
                      <Td>{log.device_count}</Td>
                      <Td>
                        <StatusChip variant={log.status === 'success' ? 'green' : 'red'}>
                          {log.status}
                        </StatusChip>
                      </Td>
                      <Td>{log.error_message || '-'}</Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && userIsCompanyAdmin && (
        <>
          {auditLoading ? (
            <LoadingText>Loading activity logs...</LoadingText>
          ) : auditLogs.length === 0 ? (
            <EmptyState>
              <p>No activity logs</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>User</Th>
                    <Th>Action</Th>
                    <Th>Resource</Th>
                    <Th>IP Address</Th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <Td>{new Date(log.created_at).toLocaleString()}</Td>
                      <Td>{log.user_name || log.user_email}</Td>
                      <Td><InlineCode>{log.action}</InlineCode></Td>
                      <Td>
                        {log.resource_type}
                        {log.resource_id && (
                          <MutedText> ({log.resource_id.slice(0, 8)}...)</MutedText>
                        )}
                      </Td>
                      <Td>{log.ip_address}</Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && userIsCompanyAdmin && (
        <form onSubmit={handleSaveSettings}>
          <DetailGrid>
            <DetailCard>
              <h2>Company Information</h2>
              <FormGroup>
                <Label htmlFor="name">Company Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={settingsForm.name}
                  onChange={handleSettingsChange}
                  placeholder="Enter company name"
                />
              </FormGroup>
            </DetailCard>

            {userIsSuperAdmin && (
              <DetailCard>
                <h2>Billing</h2>
                <FormGroup>
                  <Label htmlFor="billing_method">Billing Method</Label>
                  <Select
                    id="billing_method"
                    name="billing_method"
                    value={settingsForm.billing_method}
                    onChange={handleSettingsChange}
                  >
                    <option value="none">None</option>
                    <option value="stripe">Stripe</option>
                    <option value="odoo">Odoo</option>
                    <option value="manual">Manual</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="price_per_device">Price per Device</Label>
                  <Input
                    type="number"
                    id="price_per_device"
                    name="price_per_device"
                    value={settingsForm.price_per_device}
                    onChange={handleSettingsChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </FormGroup>
              </DetailCard>
            )}

          </DetailGrid>

          <AuditLogWrap>
            <Button type="submit" disabled={updateCompanyMutation.isPending}>
              {updateCompanyMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </AuditLogWrap>
        </form>
      )}
      {/* Edit Gateway Modal */}
      {editGatewayTarget && (
        <ModalOverlay onClick={() => { setEditGatewayTarget(null); updateGatewayMutation.reset(); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Edit Gateway</H2Title>
              <ModalCloseButton
                onClick={() => { setEditGatewayTarget(null); updateGatewayMutation.reset(); }}
                aria-label="Close"
              >
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {updateGatewayMutation.isError && (
                <SpacedAlert variant="error">
                  {(updateGatewayMutation.error as any)?.response?.data?.error || 'Failed to update gateway.'}
                </SpacedAlert>
              )}
              <FormGroup>
                <Label>Name</Label>
                <Input
                  value={editGatewayForm.name}
                  onChange={(e) => setEditGatewayForm(f => ({ ...f, name: e.target.value }))}
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label>Endpoint URL</Label>
                <Input
                  value={editGatewayForm.endpointUrl}
                  onChange={(e) => setEditGatewayForm(f => ({ ...f, endpointUrl: e.target.value }))}
                  placeholder="e.g. http://10.128.0.3:3001"
                />
                <HelperText>Internal URL the API uses to reach this gateway. Use the private IP for VM-to-VM traffic.</HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => { setEditGatewayTarget(null); updateGatewayMutation.reset(); }}>
                Cancel
              </Button>
              <Button
                onClick={() => updateGatewayMutation.mutate({ gatewayId: editGatewayTarget.id, name: editGatewayForm.name, endpointUrl: editGatewayForm.endpointUrl || undefined }, { onSuccess: () => setEditGatewayTarget(null) })}
                disabled={!editGatewayForm.name.trim() || updateGatewayMutation.isPending}
              >
                {updateGatewayMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Delete Gateway Confirm Modal */}
      {deleteGatewayTarget && (
        <ModalOverlay onClick={() => { setDeleteGatewayTarget(null); deleteGatewayMutation.reset(); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Delete Gateway</H2Title>
              <ModalCloseButton
                onClick={() => { setDeleteGatewayTarget(null); deleteGatewayMutation.reset(); }}
                aria-label="Close"
              >
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {deleteGatewayMutation.isError && (
                <SpacedAlert variant="error">
                  {(deleteGatewayMutation.error as any)?.response?.data?.error || 'Failed to delete gateway.'}
                </SpacedAlert>
              )}
              <p>Delete <strong>{deleteGatewayTarget.name}</strong>? This will remove the gateway and all its API keys. This cannot be undone.</p>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => { setDeleteGatewayTarget(null); deleteGatewayMutation.reset(); }}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteGatewayMutation.mutate(deleteGatewayTarget.id)}
                disabled={deleteGatewayMutation.isPending}
              >
                {deleteGatewayMutation.isPending ? 'Deleting…' : 'Delete Gateway'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Create Gateway Modal */}
      {showCreateGatewayModal && (
        <ModalOverlay onClick={() => setShowCreateGatewayModal(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Create Gateway</H2Title>
              <ModalCloseButton onClick={() => setShowCreateGatewayModal(false)} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label htmlFor="gateway_name">Gateway Name</Label>
                <Input
                  type="text"
                  id="gateway_name"
                  value={createGatewayForm.name}
                  onChange={(e) => setCreateGatewayForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., My Gateway"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="gateway_provider">Provider</Label>
                <Select
                  id="gateway_provider"
                  value={createGatewayForm.provider}
                  onChange={(e) => setCreateGatewayForm(prev => ({ ...prev, provider: e.target.value }))}
                >
                  <option value="nrf_cloud">nRF Cloud</option>
                  <option value="aws_iot">AWS IoT</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="gateway_endpoint">Endpoint URL</Label>
                <Input
                  type="url"
                  id="gateway_endpoint"
                  value={createGatewayForm.endpointUrl}
                  onChange={(e) => setCreateGatewayForm(prev => ({ ...prev, endpointUrl: e.target.value }))}
                  placeholder="e.g., https://gateway.example.com"
                />
                <HelperText>Optional — can be configured later</HelperText>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setShowCreateGatewayModal(false)}>Cancel</Button>
              <Button
                onClick={() => createGatewayMutation.mutate({
                  companyId: id!,
                  name: createGatewayForm.name,
                  provider: createGatewayForm.provider,
                  endpointUrl: createGatewayForm.endpointUrl || undefined,
                })}
                disabled={!createGatewayForm.name || createGatewayMutation.isPending}
              >
                {createGatewayMutation.isPending ? 'Creating...' : 'Create Gateway'}
              </Button>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default CompanyDetail;
